import { InjectionToken } from '@angular/core';

export interface RealtimeSocketLike {
  on(event: string, listener: (payload?: unknown) => void): this;
  emit(event: string, payload?: unknown, acknowledgement?: (payload: unknown) => void): this;
  connect(): this;
  disconnect(): this;
  removeAllListeners(): this;
}

export interface RealtimeSocketFactoryOptions {
  transports: readonly string[];
  token: string;
  userId: string;
}

export type RealtimeSocketFactory = (
  url: string,
  options: RealtimeSocketFactoryOptions
) => RealtimeSocketLike;

interface GlobalSocketIoClient {
  connect: (
    url: string,
    options: {
      autoConnect: boolean;
      transports: string[];
      path: string;
      reconnection: boolean;
      reconnectionAttempts: number;
      reconnectionDelay: number;
      reconnectionDelayMax: number;
      timeout: number;
      query: string;
    }
  ) => RealtimeSocketLike;
}

export const REALTIME_SOCKET_FACTORY = new InjectionToken<RealtimeSocketFactory>(
  'REALTIME_SOCKET_FACTORY',
  {
    factory: () => defaultRealtimeSocketFactory
  }
);

export function defaultRealtimeSocketFactory(
  url: string,
  options: RealtimeSocketFactoryOptions
): RealtimeSocketLike {
  const socketIoClient = (window as Window & { io?: GlobalSocketIoClient }).io;

  if (!socketIoClient?.connect) {
    throw new Error('Socket.IO v2 client script is not loaded.');
  }

  return socketIoClient.connect(normalizeRealtimeSocketUrl(url), {
    autoConnect: false,
    transports: [...options.transports],
    path: '/socket.io',
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 5000,
    timeout: 10000,
    query: `token=${encodeURIComponent(options.token)}&userId=${encodeURIComponent(options.userId)}`
  });
}

export function normalizeRealtimeSocketUrl(url: string): string {
  const parsedUrl = new URL(url, window.location.origin);

  if (isCanonicalMonitorHost(parsedUrl.hostname)) {
    parsedUrl.port = '';
  }

  return parsedUrl.toString();
}

function isCanonicalMonitorHost(hostname: string): boolean {
  return hostname === 'api-monitor.ggtaxi.com' || hostname === 'socket-monitor.ggtaxi.com';
}
